// =============================================================================
// GeoSaver — Auth Store (Zustand) — CORS FIX APPLIED
// Manages Firebase auth state + JWT token lifecycle
// =============================================================================

import { create } from 'zustand'
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  User as FirebaseUser,
} from 'firebase/auth'
import { auth } from '@/lib/firebase'
import { API_BASE } from '@/hooks/useApi'

// ── Types ──────────────────────────────────────────────────────────────────

interface AuthState {
  // State
  firebaseUser: FirebaseUser | null
  accessToken: string | null
  _refreshToken: string | null
  isLoading: boolean
  error: string | null
  
  // Actions
  loginWithGoogle: () => Promise<void>
  loginWithEmail: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  refreshJwtToken: () => Promise<boolean>
  clearError: () => void
}

// ── Store ──────────────────────────────────────────────────────────────────

export const useAuthStore = create<AuthState>((set, get) => ({
  firebaseUser: null,
  accessToken: localStorage.getItem('access_token'),
  _refreshToken: localStorage.getItem('refresh_token'),
  isLoading: false,
  error: null,

  // ── Google Sign-In ──────────────────────────────────────────────────
  loginWithGoogle: async () => {
    set({ isLoading: true, error: null })
    try {
      const provider = new GoogleAuthProvider()
      provider.addScope('profile')
      provider.addScope('email')
      
      const result = await signInWithPopup(auth, provider)
      const firebaseToken = await result.user.getIdToken()
      
      // Exchange Firebase token for JWT
      const response = await fetch(`${API_BASE}/auth/login/firebase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',  // ← CORS FIX: Add this for cross-origin requests
        body: JSON.stringify({ id_token: firebaseToken }),
      })

      if (!response.ok) {
        throw new Error(`Auth failed: ${response.status}`)
      }

      const data = await response.json()
      
      // Store JWT tokens
      localStorage.setItem('access_token', data.access_token)
      localStorage.setItem('refresh_token', data.refresh_token)
      
      set({
        firebaseUser: result.user,
        accessToken: data.access_token,
        _refreshToken: data.refresh_token,
        isLoading: false,
        error: null,
      })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Google login failed'
      set({ error: message, isLoading: false })
      throw err
    }
  },

  // ── Email/Password Login ────────────────────────────────────────────
  loginWithEmail: async (email: string, password: string) => {
    set({ isLoading: true, error: null })
    try {
      const result = await signInWithEmailAndPassword(auth, email, password)
      const firebaseToken = await result.user.getIdToken()
      
      const response = await fetch(`${API_BASE}/auth/login/firebase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',  // ← CORS FIX: Add this for cross-origin requests
        body: JSON.stringify({ id_token: firebaseToken }),
      })

      if (!response.ok) {
        throw new Error(`Auth failed: ${response.status}`)
      }

      const data = await response.json()
      
      localStorage.setItem('access_token', data.access_token)
      localStorage.setItem('refresh_token', data.refresh_token)
      
      set({
        firebaseUser: result.user,
        accessToken: data.access_token,
        _refreshToken: data.refresh_token,
        isLoading: false,
        error: null,
      })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Email login failed'
      set({ error: message, isLoading: false })
      throw err
    }
  },

  // ── Logout ──────────────────────────────────────────────────────────
  logout: async () => {
    try {
      await signOut(auth)
    } finally {
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      set({
        firebaseUser: null,
        accessToken: null,
        _refreshToken: null,
        error: null,
      })
    }
  },

  // ── Token Refresh ───────────────────────────────────────────────────
  refreshJwtToken: async () => {
    const { _refreshToken } = get()
    if (!_refreshToken) return false

    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${_refreshToken}` },
        credentials: 'include',  // ← CORS FIX: Add this for cross-origin requests
      })

      if (!response.ok) {
        // Refresh failed — clear tokens but don't force logout
        useAuthStore.setState({
          accessToken: null,
          _refreshToken: null,
        })
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        return false
      }

      const data = await response.json()
      localStorage.setItem('access_token', data.access_token)
      localStorage.setItem('refresh_token', data.refresh_token)
      
      set({
        accessToken: data.access_token,
        _refreshToken: data.refresh_token,
      })
      return true
    } catch {
      // Network error — just clear tokens, don't force logout
      useAuthStore.setState({
        accessToken: null,
        _refreshToken: null,
      })
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      return false
    }
  },

  // ── Clear Error ─────────────────────────────────────────────────────
  clearError: () => set({ error: null }),
}))

// ── Auth State Listener ────────────────────────────────────────────────────
let listenerCleanup: (() => void) | null = null

export function initAuthListener() {
  if (listenerCleanup) return // already registered
  
  listenerCleanup = onAuthStateChanged(auth, (user) => {
    if (user) {
      // Restore JWT tokens from localStorage (they persist across page navigation)
      const storedAccessToken = localStorage.getItem('access_token')
      const storedRefreshToken = localStorage.getItem('refresh_token')
      
      // Keep the store in sync with Firebase auth state + JWT tokens
      useAuthStore.setState({
        firebaseUser: user,
        accessToken: storedAccessToken,
        _refreshToken: storedRefreshToken,
      })
      
      // Ensure we have a fresh ID token (Firebase rotates them automatically)
      user.getIdToken().catch(() => {
        // Silent fail — the next API call will handle re-auth
      })
    } else {
      // Firebase session expired or user signed out externally.
      // CRITICAL: Do NOT clear JWT tokens here — they may still be valid.
      // Only clear firebaseUser; the refreshJwtToken() will handle re-auth.
      const storedAccessToken = localStorage.getItem('access_token')
      const storedRefreshToken = localStorage.getItem('refresh_token')
      
      if (storedAccessToken && storedRefreshToken) {
        // Keep JWT tokens in store — they might still work
        // Wait for Firebase SDK to auto-refresh ID token before attempting backend refresh
        useAuthStore.setState({
          firebaseUser: null,
          accessToken: storedAccessToken,
          _refreshToken: storedRefreshToken,
        })
        
        // Defer refresh to avoid race condition with Firebase SDK
        setTimeout(() => {
          useAuthStore.getState().refreshJwtToken().then((success: boolean) => {
            if (!success) {
              // Refresh failed — now it's safe to clear
              useAuthStore.setState({
                firebaseUser: null,
                accessToken: null,
                _refreshToken: null,
              })
              localStorage.removeItem('access_token')
              localStorage.removeItem('refresh_token')
            }
          }).catch(() => {
            // Network error — don't clear tokens, let next API call handle it
          })
        }, 500)
      } else {
        // No stored tokens — truly logged out
        useAuthStore.setState({
          firebaseUser: null,
          accessToken: null,
          _refreshToken: null,
        })
      }
    }
  })
}

// Auto-init on module load
initAuthListener()